import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {DistanceTextureSockets} from "#material/nodes/texture/distance-texture/definition"
import {distanceTextureDefinition} from "#material/nodes/texture/distance-texture/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import {RenderNodes} from "@cm/render-nodes"
import type {ImageTextureNode} from "#material/cycles/types"
import type {ImageTextureExtension, ImageTextureProjection} from "#material/nodes/texture/tex-image/types"
import {cyclesExtension, cyclesProjection} from "#material/nodes/texture/tex-image/helpers"
import {threeRGBColor} from "#material/three/three-utils"

@registerMaterialGraphNode
export class DistanceTexture extends MaterialNode<DistanceTextureSockets> {
    public override get definition() {
        return distanceTextureDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<DistanceTextureSockets>> => {
        const {logger} = evaluator.context
        logger.warn("WARNING: Distance texture node not implemented!")
        return {color: threeRGBColor({r: 0, g: 0, b: 0})}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<DistanceTextureSockets>> => {
        const {getMeshData, getObjects} = evaluator.context
        const meshObjectData = this.parameters.settings.meshObjectData

        const targets = this.parameters.settings.targets ?? []
        const resolvedTargets: RenderNodes.Object[] = []
        for (const target of targets) resolvedTargets.push(...(await getObjects(target)))

        const {range, mergeRange, width, height, forceOriginalResolution, uvChannel, innerValue, smoothPasses} = this.parameters.settings

        if (
            range === undefined ||
            mergeRange === undefined ||
            width === undefined ||
            height === undefined ||
            uvChannel === undefined ||
            smoothPasses === undefined
        )
            throw new Error("Distance texture node requires range, mergeRange, width, and height to be defined.")

        const imageTextureNode: ImageTextureNode = {
            type: "image_texture",
            inputs: {vector: await evaluator.get(this.parameters.inputs.vector)},
            parameters: {
                colorspace: "__builtin_raw",
                extension: cyclesExtension(this.parameters.settings.extension as ImageTextureExtension),
                interpolation: "cubic",
                projection: cyclesProjection(this.parameters.settings.projection as ImageTextureProjection),
                projection_blend: this.parameters.settings.projectionBlend,
                alpha_type: undefined,
                animated: undefined,
                tiles: undefined,
                filename: undefined,
            },
            resources: {
                image: RenderNodes.computeImage({
                    type: "distanceTexture",
                    meshData: await getMeshData(meshObjectData.completeMeshData),
                    transform: meshObjectData.matrix.toArray(),
                    range,
                    mergeRange,
                    width,
                    height,
                    forceOriginalResolution,
                    uvChannel,
                    targets: resolvedTargets,
                    innerValue,
                    smoothPasses,
                }),
            },
        }

        return {color: [imageTextureNode, "color"]}
    }
}
